import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../context/ApiContext";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { Alert } from "../ui/Alert";
import { PageBuilder as PageBuilderComponent } from "../pageBuilder/PageBuilder";
import { PageBuilderProvider } from "../../context/PageBuilderContext";
import { IPageBuilder, IComponentNode } from "../../types/pageBuilder.types";

interface HomePageZoneEditorProps {
  onSuccess?: () => void;
}

export const HomePageZoneEditor: React.FC<HomePageZoneEditorProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const { api } = useApi();
  const [homePage, setHomePage] = useState<IPageBuilder | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingZone, setEditingZone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadHomePage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to get home page by type first (with includeUnpublished to get inactive pages too)
      let response;
      try {
        response = await api.get("/page-builder/pages/type/home?includeUnpublished=true");
        if (response.data.page) {
          setHomePage(response.data.page);
          return;
        }
      } catch (err: any) {
        // If 404, try to create it using add-missing-zones (creates home page)
        if (err.response?.status === 404) {
          try {
            await api.post("/page-builder/pages/add-missing-zones");
            // Try again after creating
            response = await api.get("/page-builder/pages/type/home?includeUnpublished=true");
            if (response.data.page) {
              setHomePage(response.data.page);
              return;
            }
          } catch (createErr: any) {
            // If add-missing-zones fails, try add-missing-default-pages
            try {
              await api.post("/page-builder/pages/add-missing-default-pages");
              // Try again after creating
              response = await api.get("/page-builder/pages/type/home?includeUnpublished=true");
              if (response.data.page) {
                setHomePage(response.data.page);
                return;
              }
            } catch (defaultErr: any) {
              setError(defaultErr.response?.data?.error || t("admin.setupWizard.steps.zones.errors.load"));
              return;
            }
          }
        } else {
          setError(err.response?.data?.error || t("admin.setupWizard.steps.zones.errors.load"));
          return;
        }
      }
      
      // If we get here, page still not found
      setError(t("admin.setupWizard.steps.zones.noHomePage"));
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.setupWizard.steps.zones.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  useEffect(() => {
    loadHomePage();
  }, [loadHomePage]);

  const handleEditZone = (zoneName: string) => {
    setEditingZone(zoneName);
  };

  const handleSaveZone = async (zoneLayout: IComponentNode) => {
    if (!homePage || !editingZone) return;

    try {
      setError(null);
      const currentZones = { ...(homePage.zones || {}) };
      currentZones[editingZone] = zoneLayout;

      await api.put(`/page-builder/pages/${homePage._id}`, {
        zones: currentZones,
      });

      await loadHomePage();
      setEditingZone(null);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.setupWizard.steps.zones.errors.save"));
    }
  };

  const handleCancelEdit = () => {
    setEditingZone(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error && !homePage) {
    return <Alert variant="error">{error}</Alert>;
  }

  if (editingZone && homePage) {
    const zoneContent = homePage.zones?.[editingZone] || {
      id: `zone-${editingZone}-${Date.now()}`,
      type: "container",
      props: {},
      styles: { default: {} },
      children: [],
    };

    return (
      <PageBuilderProvider initialLayout={zoneContent}>
        <div className="fixed inset-0 flex flex-col bg-gray-100 z-[9999]">
          <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t("admin.setupWizard.steps.zones.editingZone")}: {editingZone}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Page Type: <strong>home</strong>
              </p>
            </div>
            <Button variant="outline" onClick={handleCancelEdit}>
              {t("common.cancel")}
            </Button>
          </div>
          <div className="flex-1 overflow-hidden">
            <PageBuilderComponent
              initialLayout={zoneContent}
              onCancel={handleCancelEdit}
              onSave={handleSaveZone}
              zoneName={editingZone}
              pageType="home"
            />
          </div>
        </div>
      </PageBuilderProvider>
    );
  }

  const defaultZones = ["hero", "features", "benefits", "stats", "cta"];
  const existingZones = homePage ? Object.keys(homePage.zones || {}) : [];
  const allZones = Array.from(new Set([...defaultZones, ...existingZones]));

  const getZoneTitle = (zoneName: string) => {
    const translationKey = `admin.setupWizard.steps.zones.zoneNames.${zoneName}`;
    const translated = t(translationKey);
    // If translation doesn't exist, return capitalized zone name
    return translated !== translationKey ? translated : zoneName.charAt(0).toUpperCase() + zoneName.slice(1);
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-600 mb-4">
          {t("admin.setupWizard.steps.zones.homePageDescription")}
        </p>
      </div>
      {allZones.length === 0 ? (
        <Alert variant="info">
          {t("admin.setupWizard.steps.zones.noZones")}
        </Alert>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {allZones.map((zoneName) => {
            const hasContent = homePage?.zones?.[zoneName] && 
              (homePage.zones[zoneName].children?.length || 0) > 0;
            return (
              <Card
                key={zoneName}
                padding="sm"
                className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                  hasContent ? "border-green-200 bg-green-50" : ""
                }`}
                onClick={() => handleEditZone(zoneName)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{getZoneTitle(zoneName)}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {hasContent
                        ? t("admin.setupWizard.steps.zones.hasContent")
                        : t("admin.setupWizard.steps.zones.empty")}
                    </div>
                  </div>
                  {hasContent && (
                    <span className="text-green-600 text-xs">✓</span>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

